import { ColumnDef, PaginationState } from "@tanstack/react-table";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import { Loader, MoreVertical, PlusCircle, Printer, Download } from "lucide-react";
import { useState } from "react";
import { useQuery } from "react-query";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import DataTable from "@/components/datatable/Datatable";
import { motion } from "framer-motion";
import { format } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useModalState from "@/hooks/useModalState";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import * as z from "zod";
import { toast } from "sonner";
import useConfirmModal from "@/hooks/useConfirmModal";
import ConfirmModal from "@/components/modal/ConfirmModal";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getCooperativeLogoSrc } from "@/components/ui/cooperative-logo";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
});

function ExpenseCategoryForm({ isOpen, setIsOpen, refetch, record }) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: record
      ? {
          ...record,
        }
      : {
          name: "",
          description: "",
        },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    const q = record
      ? api.patch(`/expense-categories/${record.id}`, values)
      : api.post("/expense-categories", { ...values });
    return q
      .then(() => {
        refetch();
        toast.success(
          record
            ? "Expense category updated successfully"
            : "Expense category created successfully"
        );
        setIsOpen(false);
        form.reset();
      })
      .catch((e) => {
        toast.error(e.response?.data?.message || e.message);
        const errors = e?.response?.data?.meta?.errors || {};
        Object.keys(errors)?.forEach((field: any) => {
          form.setError(field, {
            message: errors[field],
          });
        });
      });
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md p-0 gap-0">
        <DialogHeader className="border-b px-4 py-2.5">
          <DialogTitle className="text-[15px]">
            {record ? "Update Expense Category" : "Add New Expense Category"}
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-full">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-8 pt-3 pb-4 px-3"
            >
              <div className="space-y-1">
                <div className="grid gap-2">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            placeholder="Enter Category Name"
                            error={fieldState?.error?.message}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter Description"
                            error={fieldState?.error?.message}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </form>
          </Form>
        </ScrollArea>

        <DialogFooter className="border-t px-3 py-2.5 ">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setIsOpen(false);
              form.reset();
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={form.formState.disabled || form.formState.isSubmitting}
            type="submit"
            size="sm"
            onClick={form.handleSubmit(onSubmit)}
          >
            {form.formState.isSubmitting && (
              <Loader className="mr-2 h-4 w-4 text-white animate-spin" />
            )}
            {record ? "Update Category" : "Add Category"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ExpenseCategories() {
  const [recordToEdit, setRecordToEdit] = useState(undefined);

  const columns: ColumnDef<any>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
              ? "indeterminate"
              : false
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Name" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center truncate gap-3">
            {row.getValue("name")}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },

    {
      accessorKey: "description",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Description" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center truncate gap-3">
            {row.getValue("description")}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },

    {
      id: "actions",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Actions" />
      ),
      cell: ({ row }) => (
        <div className="flex items-center justify-center mt-1 gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-7 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreVertical size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => {
                  setRecordToEdit(row?.original);
                }}
              >
                Update Category
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  confirmModal.open({ meta: row?.original });
                }}
              >
                Delete Category
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  const [searchText, setSearchText] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([
    {
      id: "date",
      desc: true,
    },
  ]);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const newRecordModal = useModalState();
  const confirmModal = useConfirmModal();

  const recordsQuery = useQuery({
    queryKey: [
      "expense-categories",
      {
        search: searchText,
        filter: columnFilters,
        sort: sorting,
        pageIndex,
        pageSize,
      },
    ],
    keepPreviousData: true,
    queryFn: async () => {
      const { data } = await api.get(`/expense-categories`, {
        params: {
          page_size: pageSize,
          page: pageIndex + 1,
          ...(searchText && { search: searchText }),
          filters: [
            ...columnFilters.map((e) => {
              return {
                field: e.id,
                operator: "in",
                value: e.value?.map((e) => e?.value || e),
              };
            }),
          ],
        },
      });

      return {
        items: data?.results?.map((e) => {
          return {
            ...e,
          };
        }),
        totalPages: data?.totalPages,
      };
    },
  });

  const handleDelete = (record) => {
    confirmModal.setIsLoading(true);
    return api
      .delete(`/expense-categories/${record.id}`)
      .then(() => {
        recordsQuery.refetch();
        confirmModal.close();
        toast.success("Expense category deleted successfully");
      })
      .catch((e) => {
        confirmModal.setIsLoading(false);
        toast.error(e.message);
      });
  };

  return (
    <>
      <ConfirmModal
        title={"Are you sure you want to delete?"}
        description={`This will permanently delete the expense category and cannot be undone.`}
        meta={confirmModal.meta}
        onConfirm={(meta) => {
          handleDelete(meta);
        }}
        isLoading={confirmModal.isLoading}
        open={confirmModal.isOpen}
        onClose={() => confirmModal.close()}
      />

      <div className="sm:px-2 px-2">
        {/* Header */}
        <motion.div 
          className="flex items-center justify-between gap-1.5 sm:gap-4 overflow-hidden mb-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h1
            className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold tracking-tight flex-shrink-0 whitespace-nowrap"
            style={{ color: "#050500" }}
          >
              Expense Categories Management
          </h1>
          <div className="flex items-center gap-1 sm:gap-1.5 md:gap-2 flex-shrink-0">
            <motion.button
              className="h-8 sm:h-9 rounded-md px-1.5 sm:px-2 md:px-3 text-[10px] sm:text-xs md:text-[13px] font-semibold flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 border-2 min-w-[60px] sm:min-w-[80px] md:min-w-[100px] flex-shrink-0 bg-white"
              whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
              whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
              onClick={async () => {
                try {
                  // Dynamically import jsPDF and html2canvas
                  const jsPDF = (await import('jspdf')).jsPDF;
                  const html2canvas = (await import('html2canvas')).default;
                  
                  // Fetch ALL expense categories for PDF export - use same structure as working query
                  let allData: any[] = [];
                  let currentPage = 1;
                  let hasMore = true;
                  
                  while (hasMore) {
                    const { data: pageData } = await api.get("/expense-categories", {
                      params: {
                        page_size: 100,
                        page: currentPage,
                        filters: [],
                      },
                    });
                    
                    const pageResults = pageData?.results || pageData?.data || [];
                    if (pageResults.length > 0) {
                      allData = [...allData, ...pageResults];
                      currentPage++;
                      // Stop if we got fewer results than page size (last page)
                      if (pageResults.length < 100) {
                        hasMore = false;
                      }
                    } else {
                      hasMore = false;
                    }
                  }
                  
                  const data = allData;
                  const startDate = format(new Date(), 'MMMM d, yyyy');
                  const endDate = format(new Date(), 'MMMM d, yyyy');
                  
                  // Generate table rows
                  const tableRows = data.map((cat: any, index: number) => {
                    const name = cat.name || 'N/A';
                    const description = cat.description || '-';
                    const rowBg = index % 2 === 0 ? '#f9f9f9' : '#ffffff';
                    return `<tr style="background-color: ${rowBg};">
                      <td style="border: 1px solid #ddd; padding: 8px 6px;">${name}</td>
                      <td style="border: 1px solid #ddd; padding: 8px 6px;">${description}</td>
                    </tr>`;
                  }).join('');
                  
                  // Create a hidden div for PDF content
                  const pdfDiv = document.createElement('div');
                  pdfDiv.style.position = 'fixed';
                  pdfDiv.style.left = '-9999px';
                  pdfDiv.style.top = '0';
                  pdfDiv.style.width = '1200px';
                  pdfDiv.style.background = '#fff';
                  pdfDiv.style.fontFamily = 'Arial, sans-serif';
                  pdfDiv.style.padding = '40px';
                  pdfDiv.innerHTML = `
                    <div style="margin-bottom: 30px;">
                      <div style="display: flex; align-items: center; margin-bottom: 15px;">
                        <div style="width: 120px; margin-right: 20px; display: flex; flex-direction: column; align-items: center;">
                          <img src={getCooperativeLogoSrc()} alt="IKIMINA Logo" style="max-width: 120px; height: auto; object-fit: contain;" />
                        </div>
                        <div style="flex: 1;">
                          <div style="font-size: 24px; font-weight: bold; color: #000; margin-bottom: 5px;">CAISSE D'ENTRAIDE IPRC KARONGI</div>
                          <div style="font-size: 18px; font-weight: bold; color: #000; margin-bottom: 5px;">Expense Categories Report</div>
                          <div style="font-size: 14px; color: #000; margin-bottom: 0;">${startDate} - ${endDate}</div>
                        </div>
                      </div>
                      <hr style="border: none; border-top: 3px solid #000; margin: 15px 0; width: 100%;">
                      <div style="display: block; font-size: 12px; color: #000; margin-bottom: 15px;">
                        <div style="display: block; margin-bottom: 5px;"><strong>Address:</strong> Karongi, Rwanda</div>
                        <div style="display: block; margin-bottom: 5px;"><strong>Phone:</strong> +250 783 610 386</div>
                        <div style="display: block; margin-bottom: 5px;"><strong>Email:</strong> ikiminakarongicollege@gmail.com</div>
                      </div>
                      <hr style="border: none; border-top: 1px solid #ccc; margin: 15px 0 20px 0; width: 100%;">
                    </div>
                    
                    <div style="text-align: center; font-size: 20px; font-weight: bold; margin: 20px 0; color: #050500;">
                      EXPENSE CATEGORIES REPORT
                    </div>
                    
                    <div style="margin-top: 20px; padding: 15px; background-color: #f5f5f5; border-radius: 5px; font-size: 14px;">
                      <h3 style="margin-bottom: 10px;">Summary</h3>
                      <p><strong>Total Categories:</strong> ${data.length}</p>
                    </div>
                    
                    <table style="width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 20px;">
                      <thead>
                        <tr>
                          <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Name</th>
                          <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${tableRows}
                      </tbody>
                    </table>
                    
                    <div style="margin-top: 24px; padding: 15px; background-color: #f5f5f5; border-radius: 5px; font-size: 14px;">
                      <strong>Total Categories:</strong> ${data.length} | 
                      <strong>Generated on:</strong> ${format(new Date(), 'MMMM d, yyyy')} at ${format(new Date(), 'h:mm a')}
                    </div>
                  `;
                  document.body.appendChild(pdfDiv);
                  
                  // Use html2canvas to render the div
                  const canvas = await html2canvas(pdfDiv, { scale: 2 });
                  const imgData = canvas.toDataURL('image/png');
                  const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: [canvas.width, canvas.height] });
                  pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
                  pdf.save('expense_categories_report.pdf');
                  document.body.removeChild(pdfDiv);
                  
                  toast.success('PDF report downloaded successfully');
                } catch (error: any) {
                  console.error("Error generating PDF report:", error);
                  const errorMessage = error?.response?.data?.message || error?.message || "Failed to generate PDF report";
                  toast.error(errorMessage);
                }
              }}
            >
              <motion.span
                className="flex items-center text-gray-700"
                initial={{ x: 0 }}
                whileHover={{ x: 2 }}
                transition={{ duration: 0.2 }}
              >
                <Download className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 mr-1 sm:mr-1.5 md:mr-2" />
                PDF
              </motion.span>
            </motion.button>
            <motion.button
              className="h-8 sm:h-9 rounded-md px-1.5 sm:px-2 md:px-3 text-[10px] sm:text-xs md:text-[13px] font-semibold flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 border-2 min-w-[60px] sm:min-w-[80px] md:min-w-[100px] flex-shrink-0 bg-white"
              whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
              whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
              onClick={async () => {
                try {
                  // Fetch ALL expense categories for printing - use same structure as working query
                  let allData: any[] = [];
                  let currentPage = 1;
                  let hasMore = true;
                  
                  while (hasMore) {
                    const { data: pageData } = await api.get("/expense-categories", {
                      params: {
                        page_size: 100,
                        page: currentPage,
                        filters: [],
                      },
                    });
                    
                    const pageResults = pageData?.results || pageData?.data || [];
                    if (pageResults.length > 0) {
                      allData = [...allData, ...pageResults];
                      currentPage++;
                      // Stop if we got fewer results than page size (last page)
                      if (pageResults.length < 100) {
                        hasMore = false;
                      }
                    } else {
                      hasMore = false;
                    }
                  }
                  
                  const data = allData;
                  const startDate = format(new Date(), 'MMMM d, yyyy');
                  const endDate = format(new Date(), 'MMMM d, yyyy');
                  
                  // Generate table rows
                  const tableRows = data.map((cat: any, index: number) => {
                    const name = cat.name || 'N/A';
                    const description = cat.description || '-';
                    const rowBg = index % 2 === 0 ? '#f9f9f9' : '#ffffff';
                    return `<tr style="background-color: ${rowBg};">
                      <td style="border: 1px solid #ddd; padding: 10px 8px;">${name}</td>
                      <td style="border: 1px solid #ddd; padding: 10px 8px;">${description}</td>
                    </tr>`;
                  }).join('');
                  
                  const htmlContent = `
                    <!DOCTYPE html>
                    <html>
                      <head>
                        <title>Expense Categories Report - CAISSE D'ENTRAIDE IPRC KARONGI</title>
                        <style>
                          * { margin: 0; padding: 0; box-sizing: border-box; }
                          body { 
                            font-family: Arial, sans-serif; 
                            margin: 20px;
                            color: #333;
                          }
                          .header {
                            display: flex;
                            align-items: center;
                            margin-bottom: 15px;
                          }
                          .logo-container {
                            margin-right: 20px;
                          }
                          .logo-box {
                            width: 120px;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                          }
                          .logo-box img {
                            max-width: 120px;
                            height: auto;
                            object-fit: contain;
                          }
                          .company-info {
                            flex: 1;
                          }
                          .company-name {
                            font-size: 24px;
                            font-weight: bold;
                            color: #000;
                            margin-bottom: 5px;
                          }
                          .report-title {
                            font-size: 18px;
                            font-weight: bold;
                            color: #000;
                            margin-bottom: 5px;
                          }
                          .date-range {
                            font-size: 14px;
                            color: #000;
                            margin-bottom: 0;
                          }
                          .separator-thick {
                            border: none;
                            border-top: 3px solid #000;
                            margin: 15px 0;
                            width: 100%;
                          }
                          .separator-thin {
                            border: none;
                            border-top: 1px solid #ccc;
                            margin: 15px 0 20px 0;
                            width: 100%;
                          }
                          .contact-info {
                            display: block;
                            font-size: 12px;
                            color: #000;
                            margin-bottom: 15px;
                          }
                          .contact-info div {
                            display: block;
                            margin-bottom: 5px;
                          }
                          table { 
                            width: 100%; 
                            border-collapse: collapse; 
                            margin-top: 20px;
                            font-size: 13px;
                          }
                          th { 
                            background-color: #4dbd07;
                            color: white;
                            padding: 12px 8px;
                            text-align: left;
                            font-weight: 600;
                            border: 1px solid #3d9a06;
                          }
                          td { 
                            border: 1px solid #ddd; 
                            padding: 10px 8px; 
                            text-align: left;
                          }
                          tbody tr:nth-child(even) {
                            background-color: #f9f9f9;
                          }
                          .summary {
                            margin-top: 20px;
                            padding: 15px;
                            background-color: #f5f5f5;
                            border-radius: 5px;
                            font-size: 14px;
                          }
                          @media print {
                            body { margin: 0; padding: 15px; }
                            .summary { background: #f9f9f9 !important; }
                            @page { margin: 1cm; }
                          }
                        </style>
                      </head>
                      <body>
                        <div class="header">
                          <div class="logo-container">
                            <div class="logo-box">
                              <img src={getCooperativeLogoSrc()} alt="IKIMINA Logo" />
                            </div>
                          </div>
                          <div class="company-info">
                            <div class="company-name">CAISSE D'ENTRAIDE IPRC KARONGI</div>
                            <div class="report-title">Expense Categories Report</div>
                            <div class="date-range">${startDate} - ${endDate}</div>
                          </div>
                        </div>
                        <hr class="separator-thick">
                        <div class="contact-info">
                          <div><strong>Address:</strong> Karongi, Rwanda</div>
                          <div><strong>Phone:</strong> +250 783 610 386</div>
                          <div><strong>Email:</strong> ikiminakarongicollege@gmail.com</div>
                        </div>
                        <hr class="separator-thin">
                        
                        <div style="text-align: center; font-size: 20px; font-weight: bold; margin: 20px 0; color: #050500;">
                          EXPENSE CATEGORIES REPORT
                        </div>
                        
                        <div class="summary">
                          <h3>Summary</h3>
                          <p><strong>Total Categories:</strong> ${data.length}</p>
                        </div>
                        
                        <table>
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${tableRows}
                          </tbody>
                        </table>
                        
                        <div class="summary" style="margin-top: 20px;">
                          <strong>Total Categories:</strong> ${data.length} | 
                          <strong>Generated on:</strong> ${format(new Date(), 'MMMM d, yyyy')} at ${format(new Date(), 'h:mm a')}
          </div>
                      </body>
                    </html>
                  `;
                  
                  try {
                    const printWindow = window.open('', '_blank', 'width=800,height=600');
                    if (!printWindow) {
                      toast.error("Please allow pop-ups to print the report");
                      return;
                    }
                    
                    printWindow.document.open();
                    printWindow.document.write(htmlContent);
                    printWindow.document.close();
                    
                    // Wait for content to load, then print
                    const printAfterLoad = () => {
                      if (printWindow && !printWindow.closed) {
                        printWindow.focus();
                        printWindow.print();
                      }
                    };
                    
                    // Try multiple approaches to ensure content is loaded
                    if (printWindow.document.readyState === 'complete') {
                      setTimeout(printAfterLoad, 100);
                    } else {
                      printWindow.onload = () => setTimeout(printAfterLoad, 100);
                      // Fallback timeout
                      setTimeout(printAfterLoad, 500);
                    }
                  } catch (error) {
                    console.error("Print error:", error);
                    toast.error("Failed to open print window. Please check your pop-up blocker settings.");
                  }
                } catch (error: any) {
                  console.error("Error generating print report:", error);
                  const errorMessage = error?.response?.data?.message || error?.message || "Failed to generate print report";
                  toast.error(errorMessage);
                }
              }}
            >
              <motion.span
                className="flex items-center text-gray-700"
                initial={{ x: 0 }}
                whileHover={{ x: 2 }}
                transition={{ duration: 0.2 }}
              >
                <Printer className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 mr-1 sm:mr-1.5 md:mr-2" />
                Print
              </motion.span>
            </motion.button>
            <motion.button
              onClick={() => {
                newRecordModal.open();
              }}
              className="h-8 sm:h-9 rounded-md px-1.5 sm:px-2 md:px-3 text-[10px] sm:text-xs md:text-[13px] font-semibold flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 min-w-[60px] sm:min-w-[80px] md:min-w-[120px] flex-shrink-0"
              style={{ 
                backgroundColor: '#4dbd07',
                border: 'none',
                color: 'white'
              }}
              whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
              whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
            >
              <motion.span
                className="flex items-center text-white"
                initial={{ x: 0 }}
                whileHover={{ x: 2 }}
                transition={{ duration: 0.2 }}
              >
                <PlusCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 mr-1 sm:mr-1.5 md:mr-2" />
                <span className="hidden sm:inline">New Category</span>
                <span className="sm:hidden">New</span>
              </motion.span>
            </motion.button>
          </div>
        </motion.div>

        {/* Data Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
        <DataTable
          isFetching={recordsQuery.isFetching}
          defaultColumnVisibility={{}}
          isLoading={recordsQuery.status === "loading"}
          data={[...(recordsQuery?.data?.items || [])]?.filter((e) => e) || []}
          columns={columns}
          onSearch={(e) => {
            setSearchText(e);
          }}
          sorting={sorting}
          setSorting={setSorting}
          pageCount={recordsQuery?.data?.totalPages}
          setPagination={setPagination}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setColumnFilters={setColumnFilters}
          columnFilters={columnFilters}
          facets={[]}
        />
        </motion.div>
      </div>

      <ExpenseCategoryForm
        isOpen={newRecordModal.isOpen || Boolean(recordToEdit)}
        setIsOpen={(e) => {
          newRecordModal.setisOpen(e);
          if (!e) {
            setRecordToEdit(undefined);
          }
        }}
        refetch={recordsQuery.refetch}
        record={recordToEdit}
      />
    </>
  );
}
